import { useState, useRef, useEffect } from "react";
import {
  ActivityIndicator,
  FlatList,
  View,
  Pressable,
  Platform,
} from "react-native";
import CategoryItem from "../CategoryItem";
import {
  Container,
  Content,
  ViewText,
  ViewTextInput,
  ViewButton,
  HeaderRow,
} from "./style";
import { setDoc, doc, writeBatch } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firestore } from "../../services/firebase";
import THEME from "../../config/theme";
import ViewPortProvider from "../../hooks/ViewPortProvider";
import useViewport from "../../hooks/useViewport";
import { Feather, Ionicons } from "@expo/vector-icons";
import { StandardText, SmallText } from "../../config/theme/globalStyles";
import { Provider } from "react-native-paper";
import { AlertBox } from "../AlertBox";
import ProgressModal from "../ProgressModal";
import { CommonActions } from "@react-navigation/native";
import { firestoreAutoId, removeSansSerif } from "../../utils/validators";
import { useCustomTheme } from "../../contexts/useCustomTheme";
import { ADMIN_FONT } from "../../config/theme/adminColors";
import TitlePromptModal from "../TitlePromptModal";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import CategorySettingsModal from "../CategorySettingsModal";
import HoverTooltip from "../HoverTooltip";

const CategoryList = (props) => {
  const {
    category,
    user,
    modalFunction,
    navigation,
    searchTitle,
    selectedFilter,
    onRefreshCategories,
    productCatalogOrigin,
    titleRightSlot,
    horizontalPadding,
  } = props;

  const listPaddingLeft =
    horizontalPadding != null ? `${horizontalPadding}px` : '1rem';
  const listPaddingRight = listPaddingLeft;
  const categoryTitleMargin =
    horizontalPadding != null
      ? `0rem 0rem 0rem ${horizontalPadding}px`
      : '0rem 0rem 0rem 1rem';

  const { customColor, customFont } = useCustomTheme();

  //adminRole "supportOnly" only allows access to notiications, community and comments
  const hasAdminRole = !user?.adminRole;

  const [state, setState] = useState({
    isSearchVisible: false,
    isDeleteVisible: false,
    isConfirmationVisible: false,
    isLoading: false,
    // Duplicar categoria pode levar de segundos a minutos: tem aviso próprio
    // em tela cheia (ProgressModal), não o overlay do bloco. O resultado
    // (sucesso/erro) aparece no mesmo modal.
    isCopying: false,
    copyResult: null,
    visibleAlert: false,
    message: null,
    alertSource: null, // "addCard", "editCategory", etc.
  });

  // Toda a configuração da categoria (título, ordem, visibilidade do título,
  // formato das capas, duplicar e excluir) vive neste modal.
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const showModal = () => {
    setState((prevState) => ({ ...prevState, isDeleteVisible: true }));
  };

  const cardWidth = category?.orientation === "vertical" ? 159.04 : 264;
  const title = category ? category?.title : searchTitle;

  // ── Tokens da MARCA DO CLIENTE ────────────────────────────────────────────
  // Estes controles ficam sobre o fundo temático do cliente, então cores fixas
  // do painel admin não teriam contraste garantido aqui.
  const headerIconColor = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
  const buttonBg =
    customColor?.button?.backgroundApp || THEME.COLORS.PRIMARY_900;
  const buttonText = customColor?.button?.textApp || THEME.COLORS.TEXT_BUTTON;

  // Função (e não componente) de propósito: declarado dentro do componente, um
  // <ActionButton /> seria um tipo novo a cada render e remontaria os botões,
  // zerando o estado de hover.
  const renderActionButton = ({ key, icon, label, primary, onPress }) => (
    <Pressable
      key={key}
      onPress={onPress}
      style={({ hovered, pressed }) => [
        {
          minHeight: 38,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          paddingHorizontal: 14,
          borderRadius: 10,
          borderWidth: 1,
          borderColor: primary ? buttonBg : "rgba(128,128,128,0.4)",
          backgroundColor: primary ? buttonBg : "transparent",
          cursor: "pointer",
        },
        (hovered || pressed) && { opacity: 0.85 },
      ]}
    >
      <Ionicons
        name={icon}
        size={16}
        color={primary ? buttonText : headerIconColor}
      />
      <SmallText
        numberOfLines={1}
        style={{
          // Controle EXCLUSIVO do admin: tipografia do painel, não a fonte da
          // marca do cliente. Só as CORES seguem o tema (ver bloco acima), por
          // causa do fundo temático. `system-ui` não tem peso no nome da
          // família, então o destaque vem do fontWeight.
          fontFamily: ADMIN_FONT,
          fontWeight: "600",
          // flexShrink: no RN o padrão é 0; sem isto o rótulo transbordaria a
          // pílula em vez de truncar em telas estreitas.
          flexShrink: 1,
        }}
        fontSize={13}
        color={primary ? buttonText : headerIconColor}
      >
        {label}
      </SmallText>
    </Pressable>
  );

  // ── Reordenação de cards por drag and drop (admin, apenas web) ──────────
  // Usa HTML5 DnD com um <div> cru como wrapper — seguro porque o react-
  // native-web renderiza em DOM; no app NATIVO o wrapper não é aplicado
  // (um <div> quebraria o render), então lá a lista segue estática.
  const canDragCards = !!user?.isAdmin && hasAdminRole && Platform.OS === "web";
  const [localCards, setLocalCards] = useState(null);
  const [dragState, setDragState] = useState({ from: null, over: null });
  const [showNewCardModal, setShowNewCardModal] = useState(false);

  // Ordem otimista local vale até o pai reenviar os cards já persistidos.
  useEffect(() => {
    setLocalCards(null);
  }, [category?.cards]);

  const data = localCards || category?.cards || [];

  const persistCardOrder = async (orderedCards) => {
    try {
      const batch = writeBatch(firestore);
      orderedCards.forEach((card, index) => {
        batch.set(
          doc(firestore, "categories", category?.id, "cards", card.id),
          { order: index + 1 },
          { merge: true },
        );
      });
      await batch.commit();
      onRefreshCategories?.();
    } catch (error) {
      console.error("Erro ao salvar a nova ordem dos cards:", error);
      setLocalCards(null); // desfaz o feedback otimista: volta à ordem persistida
      showAlert("Erro ao salvar a nova ordem dos cards.");
    }
  };

  const handleCardDrop = (from, to) => {
    setDragState({ from: null, over: null });
    if (from == null || to == null || from === to) return;
    const reordered = [...data];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setLocalCards(reordered); // feedback imediato; persiste em seguida
    persistCardOrder(reordered);
  };

  // ── Carrossel horizontal estilo Netflix ──────────────────────────────────
  // Rolagem REAL via ref (scrollToOffset), então funciona em qualquer largura
  // de desktop. As setas aparecem apenas quando há overflow e não estamos na
  // ponta correspondente; o swipe/scroll continua funcionando no mobile.
  const flatListRef = useRef(null);
  const metricsRef = useRef({ containerW: 0, contentW: 0, offset: 0 });
  const [edges, setEdges] = useState({
    hasOverflow: false,
    atStart: true,
    atEnd: false,
  });

  const recomputeEdges = () => {
    const { containerW, contentW, offset } = metricsRef.current;
    const hasOverflow = contentW > containerW + 1;
    const atStart = offset <= 1;
    const atEnd = offset + containerW >= contentW - 1;
    // Só re-renderiza quando algo realmente muda (evita render a cada frame de scroll).
    setEdges((prev) =>
      prev.hasOverflow === hasOverflow &&
      prev.atStart === atStart &&
      prev.atEnd === atEnd
        ? prev
        : { hasOverflow, atStart, atEnd },
    );
  };

  const handleCarouselScroll = (e) => {
    metricsRef.current.offset = e.nativeEvent.contentOffset.x;
    recomputeEdges();
  };
  const handleListLayout = (e) => {
    metricsRef.current.containerW = e.nativeEvent.layout.width;
    recomputeEdges();
  };
  const handleContentSizeChange = (contentW) => {
    metricsRef.current.contentW = contentW;
    recomputeEdges();
  };

  const scrollCarousel = (direction) => {
    const { containerW, contentW, offset } = metricsRef.current;
    const step = Math.max(containerW * 0.8, cardWidth);
    const maxOffset = Math.max(0, contentW - containerW);
    const next = Math.max(
      0,
      Math.min(
        direction === "right" ? offset + step : offset - step,
        maxOffset,
      ),
    );
    flatListRef.current?.scrollToOffset({ offset: next, animated: true });
  };

  // Função (invocada, não montada como <Componente/>) para não remontar as setas
  // — o que zeraria o estado de hover a cada frame de scroll.
  const renderArrow = (side) => {
    const isLeft = side === "left";
    return (
      <View
        key={side}
        pointerEvents="box-none"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          [isLeft ? "left" : "right"]: 0,
          justifyContent: "center",
          paddingHorizontal: 4,
          zIndex: 20,
        }}
      >
        <Pressable
          onPress={() => scrollCarousel(isLeft ? "left" : "right")}
          accessibilityRole="button"
          accessibilityLabel={isLeft ? "Voltar" : "Avançar"}
          style={({ hovered }) => ({
            width: 44,
            height: 44,
            borderRadius: 22,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: hovered ? "rgba(0,0,0,0.72)" : "rgba(0,0,0,0.55)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            transitionDuration: "150ms",
            transitionProperty: "background-color, transform",
            transform: [{ scale: hovered ? 1.08 : 1 }],
            cursor: "pointer",
          })}
        >
          <Feather
            name={isLeft ? "chevron-left" : "chevron-right"}
            size={26}
            color="#FFFFFF"
          />
        </Pressable>
      </View>
    );
  };

  const renderCarousel = () => (
    <Content style={{ position: "relative" }}>
      {edges.hasOverflow && !edges.atStart ? renderArrow("left") : null}
      <FlatList
        ref={flatListRef}
        data={data}
        style={{ paddingLeft: listPaddingLeft }}
        contentContainerStyle={{ paddingVertical: 20, paddingRight: listPaddingRight }}
        renderItem={({ item, index }) => {
          const cardItem = (
            <CategoryItem
              categoryId={category?.id}
              item={item}
              user={user || undefined}
              orientation={category?.orientation}
              selectedFilter={selectedFilter}
              productCatalogOrigin={productCatalogOrigin}
            />
          );
          if (!canDragCards) return cardItem;

          const isDragging = dragState.from === index;
          const isDropTarget =
            dragState.from !== null &&
            dragState.over === index &&
            dragState.from !== index;
          const dndAccent = customColor?.primary || THEME.COLORS.PRIMARY_800;
          // Barra indica o LADO em que o card solto vai cair (semântica do
          // splice: vindo da esquerda cai DEPOIS do alvo; da direita, ANTES).
          const showLeftBar = isDropTarget && dragState.from > index;
          const showRightBar = isDropTarget && dragState.from < index;

          return (
            <div
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(index));
                setDragState({ from: index, over: null });
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setDragState((s) =>
                  s.over === index ? s : { ...s, over: index },
                );
              }}
              onDrop={(e) => {
                e.preventDefault();
                const from = Number(e.dataTransfer.getData("text/plain"));
                handleCardDrop(Number.isFinite(from) ? from : null, index);
              }}
              onDragEnd={() => setDragState({ from: null, over: null })}
              style={{
                display: "flex",
                cursor: "grab",
                opacity: isDragging ? 0.35 : 1,
                borderLeft: `3px solid ${showLeftBar ? dndAccent : "transparent"}`,
                borderRight: `3px solid ${showRightBar ? dndAccent : "transparent"}`,
                transition: "opacity 120ms ease",
              }}
            >
              {cardItem}
            </div>
          );
        }}
        extraData={canDragCards ? dragState : undefined}
        keyExtractor={(item, index) => item?.id || `${category?.id}-${index}`}
        horizontal
        showsHorizontalScrollIndicator={false}
        onScroll={handleCarouselScroll}
        scrollEventThrottle={16}
        onLayout={handleListLayout}
        onContentSizeChange={handleContentSizeChange}
      />
      {edges.hasOverflow && !edges.atEnd ? renderArrow("right") : null}
    </Content>
  );

  const saveCategorySettings = async ({
    title,
    order,
    orientation,
    hideTitle,
  }) => {
    try {
      setState((prevState) => ({ ...prevState, isLoading: true }));
      const categoryRef = doc(firestore, "categories", category?.id);
      await setDoc(
        categoryRef,
        { title, order, orientation, hideTitle: !!hideTitle },
        { merge: true },
      );
      setShowSettingsModal(false);
      showAlert("Categoria editada com sucesso! :)", "editCategory");
    } catch (error) {
      console.error("Erro ao salvar as configurações da categoria:", error);
      showAlert("Erro ao salvar as configurações da categoria.");
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const showAlert = (message, source = null) => {
    setState((prevState) => ({
      ...prevState,
      visibleAlert: true,
      message: message,
      alertSource: source,
    }));
  };

  const archiveCategory = async () => {
    const categoryRef = doc(firestore, "categories", category?.id);
    await setDoc(
      categoryRef,
      {
        archived: true,
      },
      { merge: true },
    );
    showAlert("Categoria Arquivada com sucesso! :)", "editCategory");
  };

  const reloadPage = () => {
    if (
      state.alertSource === "addCard" ||
      state.alertSource === "editCategory"
    ) {
      setState((prevState) => ({
        ...prevState,
        isDeleteVisible: false,
        visibleAlert: false,
        message: null,
        alertSource: null,
      }));
      onRefreshCategories?.();
      return;
    }

    if (productCatalogOrigin) {
      hideAlert();
      onRefreshCategories?.();
      return;
    }

    hideAlert();
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: "Conteudo" }],
      }),
    );
  };

  const publishCategory = async () => {
    const categoryRef = doc(firestore, "categories", category?.id);
    await setDoc(
      categoryRef,
      {
        archived: false,
      },
      { merge: true },
    );
    showAlert("Categoria Publicada com sucesso! :)", "editCategory");
  };

  // Título agora é OBRIGATÓRIO na criação do módulo (via TitlePromptModal), com
  // opção de ocultar do aluno mantendo-o para busca/filtros/acesso.
  const addCard = async (title, hideTitle) => {
    // Novo card SEMPRE na última posição (maior ordem + 1). Antes usava
    // state.allCards.length + 1, mas allCards nunca era populado — todo card
    // nascia com ordem 1 e caía no meio da categoria (ticket de suporte).
    const orders = (category?.cards || [])
      .map((c) => Number(c?.order))
      .filter((n) => Number.isFinite(n));
    const nextOrder = (orders.length ? Math.max(...orders) : 0) + 1;

    const cardsRef = doc(
      firestore,
      "categories",
      category?.id,
      "cards",
      firestoreAutoId(),
    );
    await setDoc(
      cardsRef,
      {
        title,
        hideTitle: !!hideTitle,
        description: "",
        order: nextOrder,
        archived: true,
      },
      { merge: true },
    );
    showAlert("Módulo adicionado com sucesso! :)", "addCard");
  };

  const hideAlert = () => {
    setState((prevState) => ({
      ...prevState,
      isDeleteVisible: false,
      alertSource: null,
    }));
  };

  const hideAlertConfirmation = () => {
    setState((prevState) => ({ ...prevState, isConfirmationVisible: false }));
  };

  const yesAlert = async () => {
    try {
      setState((prevState) => ({ ...prevState, isLoading: true }));

      const functions = getFunctions();
      const deleteContent = httpsCallable(functions, "deleteContent", {
        timeout: 540000,
      });

      const result = await deleteContent({
        categoryId: category?.id,
      });

      if (result?.data?.success) {
        setState((prevState) => ({
          ...prevState,
          isConfirmationVisible: true,
        }));
      } else {
        showAlert("Erro ao excluir a categoria.");
      }
    } catch (error) {
      console.error("Erro ao chamar função deleteContent:", error);
      showAlert("Erro ao excluir a categoria.");
    } finally {
      // O modal de confirmação fica aberto durante a exclusão (o botão mostra o
      // spinner) e só sai daqui — antes o AlertBox se fechava sozinho no clique.
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        isDeleteVisible: false,
      }));
    }
  };

  const yesAlert2 = () => {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: "Conteudo" }],
      }),
    );
  };

  const noAlert = () => {
    setState((prevState) => ({ ...prevState, isDeleteVisible: false }));
  };

  const copyFailed = {
    variant: "error",
    title: "Não foi possível duplicar a categoria",
    description: "Tente de novo. Se o erro continuar, fale com o suporte.",
  };

  // O resultado aparece no mesmo modal do progresso, no centro da tela. O
  // AlertBox abria dentro do bloco da categoria original, que a cópia empurra
  // para baixo, e o "OK" ficava fora da tela.
  const handleCopyCategory = async () => {
    try {
      setState((prev) => ({ ...prev, isCopying: true, copyResult: null }));
      const functions = getFunctions();
      const copyContent = httpsCallable(functions, "copyContent", {
        timeout: 540000,
      });

      const result = await copyContent({
        sourceCategoryId: category?.id,
      });

      if (result?.data?.success) {
        setState((prev) => ({
          ...prev,
          copyResult: {
            variant: "success",
            title: "Categoria duplicada",
            description: `"${category?.title || "Categoria"} (Cópia)" foi criada como arquivada. Publique quando ela estiver pronta.`,
          },
        }));
        onRefreshCategories?.();
      } else {
        setState((prev) => ({ ...prev, copyResult: copyFailed }));
      }
    } catch (error) {
      console.error("Erro ao copiar categoria:", error);
      setState((prev) => ({ ...prev, copyResult: copyFailed }));
    } finally {
      setState((prev) => ({ ...prev, isCopying: false }));
    }
  };

  return user?.isAdmin ? (
    <ViewPortProvider>
      <Provider>
        <Container>
          <HeaderRow>
            <ViewTextInput>
              <ViewText>
                <StandardText
                  style={{
                    fontFamily:
                      customFont && customFont.fontFamilyTitle
                        ? removeSansSerif(customFont.fontFamilyTitle)
                        : THEME.FONTFAMILY.BOLD,
                  }}
                  color={
                    customColor && customColor.text && customColor.text.app
                      ? customColor.text.app
                      : THEME.COLORS.TITLE_MAIN
                  }
                  textAlign="flex-start"
                  numberOfLines={1}
                  padding="0rem 1rem"
                >
                  {category?.order}
                </StandardText>
              </ViewText>
              {/* flexShrink: título longo encolhe em vez de empurrar a
                    engrenagem para fora da linha. */}
              <ViewText style={{ flexShrink: 1, minWidth: 0 }}>
                <StandardText
                  style={{
                    fontFamily:
                      customFont && customFont.fontFamilyTitle
                        ? removeSansSerif(customFont.fontFamilyTitle)
                        : THEME.FONTFAMILY.BOLD,
                  }}
                  color={
                    customColor && customColor.text && customColor.text.app
                      ? customColor.text.app
                      : THEME.COLORS.TITLE_MAIN
                  }
                  textAlign="flex-start"
                  numberOfLines={1}
                >
                  {category?.title}
                </StandardText>
              </ViewText>
              {/* Sinaliza que o título não aparece para o aluno — o admin
                    sempre vê o nome, senão não daria para identificar a linha. */}
              {category?.hideTitle && hasAdminRole ? (
                <ViewText style={{ marginLeft: 10 }}>
                  <HoverTooltip label="Título oculto para os alunos">
                    <Ionicons
                      name="eye-off-outline"
                      size={16}
                      color={headerIconColor}
                      style={{ opacity: 0.55 }}
                    />
                  </HoverTooltip>
                </ViewText>
              ) : null}
              {/* Um único acesso à configuração da categoria: nome, ordem,
                  visibilidade do título, formato das capas, duplicar e excluir. */}
              {hasAdminRole && (
                <ViewText style={{ marginLeft: 8 }}>
                  <HoverTooltip
                    label="Configurações da categoria"
                    onPress={() => setShowSettingsModal(true)}
                    style={{ padding: 4 }}
                  >
                    <Ionicons
                      name="settings-outline"
                      size={22}
                      color={headerIconColor}
                    />
                  </HoverTooltip>
                </ViewText>
              )}
            </ViewTextInput>
          </HeaderRow>
          {hasAdminRole && (
            <ViewButton>
              {category?.archived === false &&
                renderActionButton({
                  key: "archive",
                  icon: "archive-outline",
                  label: "Arquivar categoria",
                  onPress: archiveCategory,
                })}
              {category?.archived === true &&
                renderActionButton({
                  key: "publish",
                  icon: "cloud-upload-outline",
                  label: "Publicar categoria",
                  onPress: publishCategory,
                })}
              {renderActionButton({
                key: "new-card",
                icon: "add",
                label: "Novo card",
                primary: true,
                onPress: () => setShowNewCardModal(true),
              })}
            </ViewButton>
          )}
          {renderCarousel()}
          <CategorySettingsModal
            visible={showSettingsModal}
            category={category}
            isSaving={state.isLoading}
            onClose={() => setShowSettingsModal(false)}
            onSave={saveCategorySettings}
            onCopy={() => {
              setShowSettingsModal(false);
              handleCopyCategory();
            }}
            onDelete={() => {
              setShowSettingsModal(false);
              showModal();
            }}
          />
          <TitlePromptModal
            visible={showNewCardModal}
            entityLabel="módulo"
            onCancel={() => setShowNewCardModal(false)}
            onConfirm={async (title, hideTitle) => {
              setShowNewCardModal(false);
              await addCard(title, hideTitle);
            }}
          />
          <ConfirmDeleteModal
            visible={state.isDeleteVisible}
            title="Excluir esta categoria inteira?"
            entityLabel="Categoria"
            entityName={category?.title || "Sem título"}
            description={
              "Todos os cards desta categoria e os conteúdos dentro deles serão removidos permanentemente — isso inclui todos os vídeos, áudios, PDFs e imagens de capa."
            }
            warning="Esta ação não pode ser desfeita."
            confirmLabel="Excluir categoria"
            cancelLabel="Cancelar"
            isLoading={state.isLoading}
            onConfirm={yesAlert}
            onCancel={noAlert}
          />
          <AlertBox
            variant="admin"
            message={"Categoria excluída com sucesso!!"}
            visible={state.isConfirmationVisible}
            leftButton={"OK"}
            leftButtonFunction={yesAlert2}
            onClose={hideAlertConfirmation}
          ></AlertBox>
          {state.visibleAlert && (
            <AlertBox
              variant="admin"
              message={state.message}
              visible={state.visibleAlert}
              onClose={hideAlert}
              leftButtonFunction={reloadPage}
              leftButton={"OK"}
            ></AlertBox>
          )}
          <ProgressModal
            visible={state.isCopying || !!state.copyResult}
            title={`Duplicando "${category?.title || "categoria"}"`}
            description="Copiando os módulos, as páginas e os conteúdos de cada uma. Em categorias grandes isso pode levar alguns minutos; avisamos quando terminar."
            result={state.isCopying ? null : state.copyResult}
            onClose={() => setState((prev) => ({ ...prev, copyResult: null }))}
          />
          {state.isLoading && (
            <View
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              }}
            >
              <ActivityIndicator
                size="large"
                color={
                  customColor && customColor.primary
                    ? customColor.primary
                    : THEME.COLORS.PRIMARY_800
                }
              />
            </View>
          )}
        </Container>
      </Provider>
    </ViewPortProvider>
  ) : (
    <ViewPortProvider>
      <Container>
        <HeaderRow>
          <StandardText
            fontSize={
              customFont && customFont.fontMD
                ? customFont.fontMD
                : THEME.FONTSIZE.MEDIUM
            }
            style={{
              fontFamily:
                customFont && customFont.fontFamilyTitle
                  ? customFont.fontFamilyTitle
                  : THEME.FONTFAMILY.BOLD,
            }}
            color={
              customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TITLE_MAIN
            }
            textAlign="left"
            numberOfLines={1}
            margin={categoryTitleMargin}
          >
            {/* Aluno: título oculto quando a categoria está marcada como
                hideTitle (continua valendo para busca/filtros/acesso). */}
            {category?.hideTitle ? "" : title}
          </StandardText>
          {titleRightSlot}
        </HeaderRow>
        {renderCarousel()}
      </Container>
    </ViewPortProvider>
  );
};

export default CategoryList;
